/**
 * Storefront LAYOUT — the data-driven section/page composition (Layer 2 of the
 * white-label model; see docs/TENANT_CUSTOMIZATION.md and the plan). A tenant's
 * storefront is an ordered list of typed sections per page, so structurally
 * different storefronts (Shopee-style home, corporate landing, marketplace with
 * extra pages) are CONFIG, not copied page code.
 *
 * Mirrors the proven `StorefrontPageSection` discriminated-union pattern already
 * in `storefront-content.ts` (rendered by a switch), lifted from "inside one
 * page" to "the whole storefront". Rides the same `storefront` block on
 * `tenant_config.features` — no new table, no new member route.
 *
 * DISCIPLINE (keep this bounded — it is a layout editor, never a CMS):
 *  - `config` holds only INSTANCE knobs + `source` pointers into the existing
 *    CMS sub-blocks (heroSlides, bottomBanners, …); it NEVER inlines bulk
 *    content or raw markup. The CMS blocks stay the single source of truth.
 *  - Section TYPES are a closed core registry. A new reusable look = a new core
 *    type (a preset for ALL tenants), reviewed like code — never a per-tenant
 *    one-off. Only this file's types are currently the storefront-block set;
 *    landing/marketplace-chrome types are added in Phase 3 as tenants need them.
 *  - All numeric knobs are bounded unions, clamped on read; all class lookups
 *    use static Tailwind strings (never `bg-${x}` interpolation, which purges).
 */

// Type-only import (no runtime cycle with storefront-content, which imports our
// values) — the closed icon allow-list shared with the CMS.
import type { StorefrontIconKey } from './storefront-content';

/** Per-section presentation knobs (optional everywhere → falls through to baseline). */
export type SectionStyle = {
  /** Band background token. */
  bg?: 'page' | 'card' | 'tint' | 'transparent';
  /** Vertical rhythm around the band. */
  spacing?: 'tight' | 'normal' | 'loose';
};

type SectionBase<T extends string, C> = {
  type: T;
  /** Stable id — React key, admin drag handle, smooth-scroll anchor. */
  id: string;
  enabled: boolean;
  config: C;
  style?: SectionStyle;
};

/**
 * The section union. Every type here maps to an EXISTING storefront component
 * (no speculative types). `config` references CMS sub-blocks via `source`/keys.
 */
export type StorefrontSection =
  /** Rotating hero carousel (reads cfg.heroSlides) + optional stacked side tiles. */
  | SectionBase<'hero', { sideBanners?: boolean; autoAdvanceMs?: number }>
  /** Flash-sale strip (reads useFlashSale). */
  | SectionBase<'flashSale', { title?: string; seeAllHref?: string }>
  /** Featured packages strip (reads useFeaturedPackages — admin-curated product ids). */
  | SectionBase<'featuredPackages', { heading?: string; subheading?: string; cols?: 3 | 4 | 5 }>
  /** Category tile grid (reads useCategories). */
  | SectionBase<'categoryGrid', { cols?: 4 | 8 | 10; limit?: number; linkBase?: string }>
  /** "Just For You" infinite product grid (reads useCatalog). */
  | SectionBase<'productFeed', { title?: string; cols?: 3 | 4 | 6; pageSize?: number }>
  /** A horizontal carousel row of products (existing ProductRow component). */
  | SectionBase<
      'productRow',
      {
        title: string;
        source: 'bestsellers' | 'newArrivals' | 'topSelling';
        showPv?: boolean;
      }
    >
  /** Promo banner strip — names which CMS banner list to render. */
  | SectionBase<
      'bannerGroup',
      {
        source: 'bottomBanners' | 'homeSideBanners' | 'campaignBanners';
        cols?: 1 | 2 | 3;
      }
    >
  // --- Landing blocks (corporate / landing-first tenants) ---------------------
  /**
   * One parameterized text+CTA band, three looks via `variant`. The deliberate
   * place inline landing copy lives (like HeroSlide/PromoBanner) — bodyParas is
   * capped at render, featureCards is a short bounded list (≤4).
   */
  | SectionBase<
      'richLanding',
      {
        variant: 'corporateHero' | 'welcome' | 'ctaBanner';
        eyebrow?: string;
        heading: string;
        subheading?: string;
        bodyParas?: string[];
        backgroundImageKey?: string;
        overlay?: boolean;
        align?: 'center' | 'left';
        primaryCta?: { label: string; href: string };
        secondaryCta?: { label: string; href: string };
        featureCards?: { icon: StorefrontIconKey; title: string; body: string }[];
      }
    >
  /** Icon-circle card grid on a tint band (e.g. "Core Values"). */
  | SectionBase<
      'valuesGrid',
      {
        heading: string;
        subheading?: string;
        cols?: 3 | 4;
        items: { icon: StorefrontIconKey; title: string; body: string }[];
      }
    >
  /** Numbered step cards with arrow connectors (numerals derived from index). */
  | SectionBase<
      'howItWorks',
      {
        heading: string;
        subheading?: string;
        steps: { title: string; body: string }[];
      }
    >
  /** Membership tier cards — reads the `membershipTiers` CMS block (not inline). */
  | SectionBase<
      'packages',
      {
        heading: string;
        subheading?: string;
        source?: 'membershipTiers';
        cols?: 3 | 4;
      }
    >
  /** Testimonial cards — reads the existing `company.testimonials` CMS block. */
  | SectionBase<
      'testimonials',
      {
        heading: string;
        subheading?: string;
        cols?: 2 | 3;
        source?: 'company';
      }
    >
  /**
   * Stairstep ranks with icons + achiever counts (reads the public `/ranks`).
   * Renders nothing while the tenant's `rank_showcase_enabled` is off.
   */
  | SectionBase<'rankShowcase', { heading?: string; subheading?: string }>
  /** Render an existing CMS informational page's article body inline. */
  | SectionBase<
      'cmsArticle',
      { pageKey: 'about' | 'becomeMember' | 'sellOnOmni' | 'merchantGuide' }
    >
  /** Vertical spacer. */
  | SectionBase<'spacer', { height: 'sm' | 'md' | 'lg' }>
  // --- Learning storefront (Udemy-style course tenants) ---------------------
  /**
   * Course hero: full-bleed photo with an inset white card (title / sub /
   * CTA). One slide in v1. `imageKey` is a storefront asset key.
   */
  | SectionBase<
      'courseHero',
      {
        title: string;
        subheading?: string;
        ctaLabel?: string;
        ctaHref?: string;
        imageKey?: string;
      }
    >
  /**
   * Value props row — icon + title + sub, no card chrome. `earnItem` is
   * appended ONLY when the storefront's `showResellBanner` is on (the one flag
   * gates all three resell surfaces: header link, this prop, the CTA band).
   */
  | SectionBase<
      'valueProps',
      {
        items: { icon: StorefrontIconKey; title: string; body: string }[];
        earnItem?: { icon: StorefrontIconKey; title: string; body: string };
      }
    >
  /** Category link bar under the header (reads useCategories; hidden when empty). */
  | SectionBase<'courseCategoryNav', { limit?: number }>
  /**
   * Course grid — the catalog feed filtered to items with a course (joins
   * `/storefront/courses` summaries onto the product feed by productId).
   * Borderless tiles per the learning handoff.
   */
  | SectionBase<
      'courseGrid',
      { title?: string; viewAllHref?: string; limit?: 4 | 8 | 12; cols?: 3 | 4 }
    >
  /** Share-and-earn tint band; rendered only when `showResellBanner` is on. */
  | SectionBase<'resellCta', { title: string; body?: string; ctaLabel?: string; ctaHref?: string }>;

export type StorefrontSectionType = StorefrontSection['type'];

/** A data-driven page: a route path + an ordered section list + chrome flags. */
export type StorefrontLayoutPage = {
  /** Router path, e.g. '/storefront'. Dynamic/transactional routes stay in code. */
  path: string;
  title?: string;
  /** Render the shared Header around the sections (default true). */
  header?: boolean;
  /** Render the shared Footer (default true). */
  footer?: boolean;
  /** Use smooth-scroll anchors (single-page landing) instead of route nav. */
  scrollNav?: boolean;
  /**
   * Header look: 'market' (default — the Shopee-style marketplace header) or
   * 'learning' (the Udemy-style header: pill search, category bar below,
   * outlined Log in + solid Sign up).
   */
  headerVariant?: 'market' | 'learning';
  /** Footer palette: 'light' (default) or 'dark' (very dark brand green). */
  footerVariant?: 'light' | 'dark';
  /** Page background: 'muted' (default #f5f5f5 marketplace) or 'white'. */
  background?: 'muted' | 'white';
  sections: StorefrontSection[];
};

/** The tenant's storefront layout. Rides `StorefrontConfig.layout`. */
export type StorefrontLayout = {
  /** Schema/preset version for future migrations. */
  version: number;
  /** Named preset the tenant selected (informational), e.g. 'omni'. */
  preset?: string;
  /** Ordered, path-keyed pages. */
  pages: StorefrontLayoutPage[];
  /**
   * Which page `path` is also served at `/` (the configurable front door):
   * storefront-first → the storefront home; landing-first → a landing page.
   */
  homePath?: string;
};

/**
 * The baseline layout — reproduces today's `home.tsx` section ORDER exactly so an
 * un-configured tenant renders the identical Omni storefront. Storefront-first:
 * `/` serves this storefront home.
 */
export const LAYOUT_DEFAULT_OMNI: StorefrontLayout = {
  version: 1,
  preset: 'omni',
  homePath: '/storefront',
  pages: [
    {
      path: '/storefront',
      sections: [
        { type: 'hero', id: 'hero', enabled: true, config: { sideBanners: true } },
        { type: 'flashSale', id: 'flashSale', enabled: true, config: {} },
        // Hidden until the admin enables + curates it (Featured Packages page);
        // the renderer returns null while disabled/empty, so it's invisible by default.
        { type: 'featuredPackages', id: 'featuredPackages', enabled: true, config: {} },
        // Same pattern: renders null until the tenant turns the ranks showcase on.
        { type: 'rankShowcase', id: 'rankShowcase', enabled: true, config: {} },
        { type: 'categoryGrid', id: 'categories', enabled: true, config: {} },
        { type: 'productFeed', id: 'feed', enabled: true, config: { title: 'Just For You' } },
        {
          type: 'bannerGroup',
          id: 'bottomBanners',
          enabled: true,
          config: { source: 'bottomBanners' },
        },
      ],
    },
  ],
};

/**
 * Resolve a stored layout blob against the baseline. Like `resolveStorefront`:
 * a missing/empty layout falls back to `LAYOUT_DEFAULT_OMNI` wholesale (the admin
 * owns the full pages list when present). Defensive on read; the Zod schema is
 * the write-time guard.
 */
export function resolveLayout(raw: Partial<StorefrontLayout> | null | undefined): StorefrontLayout {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.pages) || raw.pages.length === 0) {
    return clone(LAYOUT_DEFAULT_OMNI);
  }
  return {
    version: typeof raw.version === 'number' ? raw.version : LAYOUT_DEFAULT_OMNI.version,
    preset: typeof raw.preset === 'string' ? raw.preset : undefined,
    homePath: typeof raw.homePath === 'string' ? raw.homePath : LAYOUT_DEFAULT_OMNI.homePath,
    pages: clone(raw.pages as StorefrontLayoutPage[]),
  };
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

// ── Section library — the admin "Add section" palette source ────────────────
// A runtime catalog of every placeable section type with a human label and a
// schema-valid default `config` (types with required fields get a sensible
// placeholder; content-curated types default to empty). This MIRRORS the
// `StorefrontSection` union above — add an entry here when you add a section
// type. It exists so a newly-shipped section type can be added to an EXISTING
// tenant's stored layout from the admin Layout editor (which honors stored
// pages verbatim and never injects new defaults).
export type SectionLibraryEntry = {
  [T in StorefrontSectionType]: {
    type: T;
    /** Human label shown in the Add-section picker. */
    label: string;
    /** One-line hint of where the section's content comes from. */
    note: string;
    /** A default `config` that satisfies the write-time section schema. */
    defaultConfig: Extract<StorefrontSection, { type: T }>['config'];
  };
}[StorefrontSectionType];

export const SECTION_LIBRARY: SectionLibraryEntry[] = [
  {
    type: 'hero',
    label: 'Hero carousel',
    note: 'Rotating hero slides (CMS: Hero slides).',
    defaultConfig: { sideBanners: true },
  },
  {
    type: 'flashSale',
    label: 'Flash sale strip',
    note: 'Time-boxed sale row (CMS: Flash sale).',
    defaultConfig: {},
  },
  {
    type: 'featuredPackages',
    label: 'Featured packages',
    note: 'Curated products (Featured Packages page).',
    defaultConfig: {},
  },
  {
    type: 'categoryGrid',
    label: 'Category grid',
    note: 'Category tiles from product categories.',
    defaultConfig: {},
  },
  {
    type: 'productFeed',
    label: 'Product feed — "Just For You"',
    note: 'Infinite catalog grid.',
    defaultConfig: { title: 'Just For You' },
  },
  {
    type: 'productRow',
    label: 'Product row (carousel)',
    note: 'Horizontal product carousel.',
    defaultConfig: { title: 'Bestsellers', source: 'bestsellers' },
  },
  {
    type: 'bannerGroup',
    label: 'Banner group',
    note: 'Promo banner strip (CMS: Banners).',
    defaultConfig: { source: 'bottomBanners' },
  },
  {
    type: 'richLanding',
    label: 'Landing band (hero / CTA)',
    note: 'Text + CTA band for landing pages.',
    defaultConfig: { variant: 'ctaBanner', heading: 'Heading' },
  },
  {
    type: 'valuesGrid',
    label: 'Values grid',
    note: 'Icon-card grid — edit items after adding.',
    defaultConfig: { heading: 'Our Values', items: [] },
  },
  {
    type: 'howItWorks',
    label: 'How it works',
    note: 'Numbered steps — edit steps after adding.',
    defaultConfig: { heading: 'How It Works', steps: [] },
  },
  {
    type: 'packages',
    label: 'Membership packages',
    note: 'Tier cards (CMS: Membership tiers).',
    defaultConfig: { heading: 'Membership Packages' },
  },
  {
    type: 'testimonials',
    label: 'Testimonials',
    note: 'Quote cards (CMS: Company testimonials).',
    defaultConfig: { heading: 'Testimonials' },
  },
  {
    type: 'rankShowcase',
    label: 'Ranks showcase',
    note: 'Stairstep ranks + achievers (Settings: Show ranks showcase).',
    defaultConfig: {},
  },
  {
    type: 'cmsArticle',
    label: 'CMS article',
    note: "Inline an info page's article body.",
    defaultConfig: { pageKey: 'about' },
  },
  { type: 'spacer', label: 'Spacer', note: 'Vertical spacing.', defaultConfig: { height: 'md' } },
  {
    type: 'courseHero',
    label: 'Course hero',
    note: 'Learning storefront: photo hero with inset title card + CTA.',
    defaultConfig: {
      title: 'Learn skills that sell.',
      subheading: 'Practical courses from real sellers. Start today, learn at your own pace.',
      ctaLabel: 'Browse courses',
      ctaHref: '/shop',
    },
  },
  {
    type: 'valueProps',
    label: 'Value props',
    note: 'Learning storefront: icon + title + sub row (no card chrome).',
    defaultConfig: {
      items: [
        { icon: 'clock', title: 'Learn at your own pace', body: 'Lifetime access on any device' },
        { icon: 'userCheck', title: 'Taught by real sellers', body: 'Practical lessons, no fluff' },
        { icon: 'award', title: 'Certificates', body: 'Finish a course, get certified' },
      ],
      earnItem: { icon: 'share', title: 'Earn by sharing', body: 'Invite friends, earn rewards' },
    },
  },
  {
    type: 'courseCategoryNav',
    label: 'Course category bar',
    note: 'Learning storefront: category links under the header (hidden until seeded).',
    defaultConfig: {},
  },
  {
    type: 'courseGrid',
    label: 'Course grid',
    note: 'Learning storefront: catalog feed filtered to courses, borderless tiles.',
    defaultConfig: { title: 'Featured courses', viewAllHref: '/shop', limit: 8, cols: 4 },
  },
  {
    type: 'resellCta',
    label: 'Share & earn band',
    note: 'Learning storefront: tint CTA band; shown only when Share & earn is on.',
    defaultConfig: {
      title: 'Love a course? Share it.',
      body: 'Every member gets a share link. When a friend enrolls through it, you earn a reward — no selling experience needed.',
      ctaLabel: 'See how it works',
      ctaHref: '/become-member',
    },
  },
];

/** Look up a section type's library entry (label + default config). */
export function sectionLibraryEntry(type: StorefrontSectionType): SectionLibraryEntry | undefined {
  return SECTION_LIBRARY.find((e) => e.type === type);
}

// Pick a stable, collision-free section id: the type itself, else `type-2`,
// `type-3`, … so two sections of the same type coexist (React key / anchor).
function uniqueSectionId(base: string, existing: readonly string[]): string {
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Build a fresh, schema-valid `StorefrontSection` of `type` with a unique id
 * (avoiding `existingIds`) and its default config. Powers the admin Layout
 * editor's Add-section control. Throws on an unknown type.
 */
export function createSection(
  type: StorefrontSectionType,
  existingIds: readonly string[] = [],
): StorefrontSection {
  const entry = sectionLibraryEntry(type);
  if (!entry) throw new Error(`unknown section type: ${type}`);
  // entry.type ↔ defaultConfig correlation is lost through the array lookup, so
  // the assembled object is asserted back to the union — it's correct by
  // construction (each library entry pairs a type with its own default config).
  return {
    type: entry.type,
    id: uniqueSectionId(type, existingIds),
    enabled: true,
    config: clone(entry.defaultConfig),
  } as StorefrontSection;
}
